import { Constructor } from '@pushly/models/lib/types/constructor'
import { InvalidFieldException } from '@pushly/models/lib/exceptions/invalid-field-exception'
import { MissingFieldException } from '@pushly/models/lib/exceptions/missing-field-exception'
import { sentenceCase, snakeCase } from '@pushly/models/lib/utils/change-case'

type ErrorExtractionOptions<T extends Error> = {
    parentProperty?: string
    rootModel?: Constructor<{}>
    extractReason?: (error: InvalidFieldException | MissingFieldException) => string
    errorClass?: Constructor<T>
    snakeCasePropertyName?: boolean
    fieldNamesMap?: object
    startWithProperty?: boolean
}

const ID_STR_PATTERN = /(^|\s)id(s)?($|\s)/g

export function extractFriendlyErrorMessage<T extends Error = Error>(
    error: InvalidFieldException | MissingFieldException,
    options?: ErrorExtractionOptions<T>,
): T {
    const errorClass = options?.errorClass ?? Error
    const target =
        options?.parentProperty ?? (options?.rootModel?.name === error.target ? null : snakeCase(error.target ?? ''))
    const targetStr = !target ? '' : ` of ${target}`
    const field = error.field
    const arrayAgnosticField = field.replace(/\.\d+\./g, '.*.')

    let fieldStr = field
    if (options?.snakeCasePropertyName) {
        fieldStr = snakeCase(field)
    } else if (options?.fieldNamesMap) {
        if (options.fieldNamesMap[field]) {
            // array position aware
            fieldStr = options.fieldNamesMap[field]
        } else if (options.fieldNamesMap[arrayAgnosticField]) {
            // array position agnostic
            fieldStr = options.fieldNamesMap[arrayAgnosticField]
        } else {
            fieldStr = field
                .split('.')
                .map((f) => options.fieldNamesMap?.[f] ?? f)
                .join(' ')
        }
    }

    const leader = options?.startWithProperty ? '' : 'Property '
    const reason = options?.extractReason ? options.extractReason(error) : error.failureReason

    if (options?.startWithProperty) {
        fieldStr = sentenceCase(fieldStr)
        // make sure "id" is capitalized as "ID"
        if (ID_STR_PATTERN.test(fieldStr)) {
            fieldStr = fieldStr.replace(ID_STR_PATTERN, '$1ID$2$3')
        }
    }

    return new errorClass(`${leader}${fieldStr}${targetStr} ${reason}`) as T
}
